import { useState, useEffect } from "react";

/**
 * Browser-compatible YAML frontmatter parser.
 * Handles strings, numbers, booleans and quoted strings.
 */
function parseFrontmatter(raw) {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/);
  if (!match) return { data: {}, content: raw };

  const yamlBlock = match[1];
  const content = match[2];
  const data = {};

  for (const line of yamlBlock.split(/\r?\n/)) {
    const colonIdx = line.indexOf(":");
    if (colonIdx === -1) continue;
    const key = line.slice(0, colonIdx).trim();
    let value = line.slice(colonIdx + 1).trim();
    // Strip surrounding quotes
    if (/^["'].*["']$/.test(value)) value = value.slice(1, -1);
    // Coerce booleans and numbers
    if (value === "true") value = true;
    else if (value === "false") value = false;
    else if (value !== "" && !isNaN(Number(value))) value = Number(value);
    if (key) data[key] = value;
  }

  return { data, content };
}

/**
 * Fetches an MDX/MD file from `url`, parses its frontmatter and body.
 *
 * Returns:
 *  - content   : string  – markdown body (no frontmatter)
 *  - meta      : object  – parsed frontmatter fields (e.g. descriptionSize)
 *  - loading   : boolean
 *  - error     : Error | null
 */
export default function useMdxContent(url) {
  const [content, setContent] = useState("");
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch MDX: ${res.status}`);
        return res.text();
      })
      .then((raw) => {
        if (cancelled) return;
        const { data, content: body } = parseFrontmatter(raw);
        setMeta(data);
        setContent(body);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { content, meta, loading, error };
}
